import {
    AuthActions,
    AuthGState, darkTheme, Footer,
    getAllOrgsIdsForUserByServiceName, MainLoader, Navbar, setAlert, theme, useFavoriteTheme,
    CssBaseline, ThemeProvider,
    Logout, ADTE_LOGO_SMALL_URL
} from "@leap4/frontend";
import { useCallback, useEffect, useMemo, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import {
    BACKEND_SERVICE_NAME,
    logoutUrl,
    meMultiUrl,
    requiredAuthRoles
} from "../../config/generals";
import { RootState } from "../../interfaces/root-state.interface";
import { setIsLoading } from "../../store/actions/dateRange.actions";
import Routes from "../routing/Routes";
import { urlService } from "../routing/urlsService";
import "./App.css";

const mapStateToProps = (state: RootState) => ({
    auth: state.auth,
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface IProps extends PropsFromRedux {
    history: History,
    auth: AuthGState
}

const queryClient = new QueryClient();

const styles = {
    root: {
        display: "flex",
        flexDirection: "column" as const,
        minHeight: "100vh",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
    },
    content: {
        flexGrow: 1,
        width: "100%",
        margin: "auto",
        paddingTop: "30px",
    },
};

const App = ({ auth, setAlert }: IProps) => {
    const history = useHistory();
    const [selectedTheme, setSelectedTheme] = useFavoriteTheme()
    const location = useLocation();
    const dispatch = useDispatch();
    const [bIsSidebarOpen, SetSideBarOpenState] = useState(false);
    const [isLoading, setIsLoading] = useState(true)
    const nSidebarWidth = 240;

    const getUserOrganizations = useCallback(() => {
        if (!auth.user) return [];
        const userOrgs = getAllOrgsIdsForUserByServiceName(auth.user, auth.user.organizations as string[], requiredAuthRoles, BACKEND_SERVICE_NAME)
        return userOrgs;
    }, [auth.user]);

    const handleThemeChange = useCallback((checked: boolean) => {
        const themeToShow = checked ? darkTheme : theme;
        setSelectedTheme(themeToShow)
    }, [setSelectedTheme]);

    const handleDrawerClose = useCallback(() => SetSideBarOpenState(false), []);
    const handleDrawerOpen = useCallback(() => SetSideBarOpenState(true), []);

    useEffect(() => {
        (async () => {
            try {
                await AuthActions.loadUser(dispatch, BACKEND_SERVICE_NAME, meMultiUrl)
            } finally {
                setIsLoading(false)
            }
        }
        )();
    }, [dispatch]);

    const navbarLinks = useMemo(() => [
        { text: "Dashboard", link: urlService.dashboard() },
        { text: "Reporting", link: urlService.reporting() },
        { text: "Campaigns", link: urlService.campaigns() },
        { text: "Conversion Uploads", link: urlService.conversionUploads() },
        { text: "Access Manager", link: urlService.accessManager() },
        { text: "API Keys", link: urlService.apiKeys() },
        { text: "Advanced Targeting", link: urlService.advancedTargeting() },
        { text: "Prediction Accuracy", link: urlService.predictionAccuracy() }
    ], []);

    const navbarAuthProp = useMemo(() => ({ isAuthenticated: !!auth.user }), [auth.user]);
    const navbarUiProp = useMemo(() => ({ nSidebarWidth, nNavbarHeight: 78 }), [nSidebarWidth]);
    const userOrganizations = useMemo(() => getUserOrganizations(), [getUserOrganizations]);

    const logoutAction = useCallback(async () => {
        sessionStorage.clear();
        await AuthActions.logout(dispatch, history, logoutUrl)
    }, [dispatch, history]);

    const logoutProp = useMemo(() => ({
        text: "Logout",
        action: logoutAction,
        icon: <Logout />
    }), [logoutAction]);

    if (isLoading) {
        return <MainLoader />
    }

    return (
        <>
            <ThemeProvider theme={selectedTheme}>
                <QueryClientProvider client={queryClient}>
                    <div style={styles.root}>
                        <CssBaseline />

                        {auth?.user && <Navbar
                            bIsSidebarOpen={bIsSidebarOpen}
                            handleDrawerClose={handleDrawerClose}
                            handleDrawerOpen={handleDrawerOpen}
                            auth={navbarAuthProp}
                            ui={navbarUiProp}
                            location={location.pathname}
                            links={navbarLinks}
                            orgsWithPermssions={userOrganizations}
                            updateCheckedToFather={handleThemeChange}
                            title={'Adte Conversions'}
                            logoImageUrl={ADTE_LOGO_SMALL_URL}
                            logout={logoutProp}
                        />}

                        <main style={styles.content}>
                            <div style={styles.toolbar} />
                            <Switch>
                                <Route component={Routes} />{" "}
                            </Switch>
                        </main>
                        <Footer text={'Conversions'}
                            logoUrl={ADTE_LOGO_SMALL_URL} hideTags />
                    </div>
                </QueryClientProvider>
            </ThemeProvider>

        </>
    );
};

export default connector(App);
